// Measurement CSV export button (ADR-025 / requirement 5).
//
// Exports the raw per-employee measurements as a CSV, scoped to the SAME
// q/source/site filters the table is showing. The endpoint is the highest PII
// outflow and writes a mandatory audit row, so this is a deliberate click, not
// a passive link. We fetch the CSV as an authenticated blob (handles cookie
// refresh) then trigger a client-side download — a plain <a href> can't recover
// from an expired access cookie the way the fetch path can.

import { useState } from "react";
import { useI18n } from "@faceheart/i18n";
import {
  measurementsApi,
  type EmployeeListParams,
  type ExportParams,
  type ExportRange,
} from "../../lib/api/endpoints";
import { ApiError } from "../../lib/api/client";

// The export's measurement window. Presets reuse the backend's range helper; the
// 90-day cap is enforced server-side (ADR-026), and the custom date inputs are
// bounded to [today-90d, today] so the UI can't even offer an out-of-retention day.
type RangeMode = ExportRange | "custom";

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

export function ExportButton({ filters }: { filters: EmployeeListParams }) {
  const { t } = useI18n("admin");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<RangeMode>("90d");
  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(isoDaysAgo(0));

  const today = isoDaysAgo(0);
  const floor = isoDaysAgo(90);

  async function onExport() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      // Build the window params: a preset range, or the custom from/to. qs()
      // drops empty keys, so we only send what's relevant for the chosen mode.
      const windowParams: Pick<ExportParams, "range" | "from" | "to"> =
        mode === "custom" ? { from, to } : { range: mode };
      const blob = await measurementsApi.exportCsv({ ...filters, ...windowParams });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `measurements-${stamp()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(
        e instanceof ApiError
          ? t("employees.export.failed", { code: e.code })
          : t("common:errors.network"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className="flex items-center gap-1.5 text-xs text-admin-text3">
        {t("employees.export.rangeLabel")}
        <select
          value={mode}
          onChange={(e) => setMode(e.target.value as RangeMode)}
          className="bg-admin-bg3 border border-admin-border rounded px-2 py-1 text-sm text-admin-text1 focus:outline-none focus:border-admin-cyan"
        >
          <option value="7d">{t("employeeDetail.ranges.7d")}</option>
          <option value="30d">{t("employeeDetail.ranges.30d")}</option>
          <option value="90d">{t("employeeDetail.ranges.90d")}</option>
          <option value="custom">{t("employees.export.custom")}</option>
        </select>
      </label>

      {mode === "custom" && (
        <div className="flex items-center gap-1.5 text-xs text-admin-text3">
          <input
            type="date"
            value={from}
            min={floor}
            max={to || today}
            onChange={(e) => setFrom(e.target.value)}
            className="bg-admin-bg3 border border-admin-border rounded px-2 py-1 text-sm text-admin-text1 focus:outline-none focus:border-admin-cyan"
          />
          <span>–</span>
          <input
            type="date"
            value={to}
            min={from || floor}
            max={today}
            onChange={(e) => setTo(e.target.value)}
            className="bg-admin-bg3 border border-admin-border rounded px-2 py-1 text-sm text-admin-text1 focus:outline-none focus:border-admin-cyan"
          />
        </div>
      )}

      <button
        type="button"
        onClick={() => void onExport()}
        disabled={busy}
        className="rounded border border-admin-border px-3 py-1.5 text-sm text-admin-text2 hover:bg-admin-bg3 disabled:opacity-50"
      >
        {busy ? t("employees.export.exporting") : t("employees.export.button")}
      </button>
      {error && <span className="text-xs text-admin-red">{error}</span>}
    </div>
  );
}

// YYYYMMDD-HHmm for a stable, sortable filename. Local time is fine here — it's
// a human-facing download name, not a stored value.
function stamp(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}` +
    `-${p(d.getHours())}${p(d.getMinutes())}`
  );
}
